/**
 * A book's images (decision 37; part 16a log, tables A and G): what goes into the store and what comes out of it. An add
 * takes only a stripped JPEG or PNG of the size it claims (principle 1), hashes it, and stores it under a fresh random ID
 * or finds the book's copy (dedupe within a book only). A load hashes what it read and hands out only bytes with the
 * reference's hash, their format and size read from the bytes themselves (principle 4).
 */
import { isStripped, KEPT_SIDE, readImageHeader, toHex } from '@cockatrice/core';
import type { ImageFormat, ImageReference } from '@cockatrice/core';
import { MAX_ID_TRIES } from './chapters.ts';
import { StoreRecordError } from './records.ts';
import type { ImageBytesRecord, ImageRecord } from './records.ts';
import type { Store, StoreFailure } from './store.types.ts';

/** A6: the most bytes a book's images may take together (decision 37: about 100 MB a book). */
export const BOOK_IMAGE_CAP = 100 * 1024 * 1024;

/** An image as the image worker made it: its bytes, their format and its size in pixels. */
export interface EncodedImage {
  readonly bytes: Uint8Array;
  readonly format: ImageFormat;
  readonly width: number;
  readonly height: number;
}

/**
 * The outcome of {@link ImageLibrary.add}: the reference of the image stored (A1) or found (A2, A3); bytes that aren't an
 * image this device made (A0); no room in the book (A6), with what its images use; or the store failed (A5, A9).
 */
export type ImageAddOutcome =
  | { readonly kind: 'added' | 'existing'; readonly reference: ImageReference }
  | { readonly kind: 'refused' }
  | { readonly kind: 'full'; readonly used: number }
  | StoreFailure;

/** The outcome of {@link ImageLibrary.load}: the image (G1, G7), nothing stored (G2), or bytes never to show (G3–G5). */
export type LoadedImage =
  | { readonly kind: 'image'; readonly bytes: Uint8Array; readonly format: ImageFormat; readonly width: number; readonly height: number }
  | { readonly kind: 'missing' }
  | { readonly kind: 'refused' };

/** What the library needs: the store, SHA-256 (WebCrypto's digest) and fresh random IDs, injected; tests also pass a cap. */
export interface ImageLibraryOptions {
  readonly store: Store;
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** A fresh random 128-bit ID, 32 lowercase hex digits (decision 37). */
  readonly newId: () => string;
  readonly cap?: number;
}

/**
 * A0: whether `image` is a stripped JPEG or PNG of the format and size it claims, within the cover's kept side. A file the
 * strip leaves as it is starts with its format's signature, so its header is of that format.
 */
function isMadeHere({ bytes, format, width, height }: EncodedImage): boolean {
  if (width > KEPT_SIDE.cover || height > KEPT_SIDE.cover || !isStripped(bytes, format)) return false;
  const header = readImageHeader(bytes);
  return typeof header !== 'string' && header.width === width && header.height === height;
}

/**
 * Part 24c, P7: the records of `image` in book `bookId` under a fresh ID (`newId`), its bytes' SHA-256 by `digest`, for an
 * import's own write (part 16a's table A, A12); null when A0 refuses it.
 */
export async function imageRecords(
  image: EncodedImage,
  bookId: string,
  digest: (bytes: Uint8Array) => Promise<Uint8Array>,
  newId: () => string,
): Promise<{ readonly image: ImageRecord; readonly bytes: ImageBytesRecord } | null> {
  if (!isMadeHere(image)) return null;
  const { bytes, format, width, height } = image;
  const id = newId();
  return { image: { bookId, id, sha256: toHex(await digest(bytes)), format, width, height, size: bytes.length }, bytes: { bookId, id, bytes } };
}

/** The images of every book, through one store connection. */
export class ImageLibrary {
  readonly #options: ImageLibraryOptions;

  constructor(options: ImageLibraryOptions) {
    this.#options = options;
  }

  /**
   * Table A: adds `image` to book `bookId`, or finds the book's image with the same bytes. Rejects with the digest's error,
   * or `StoreRecordError` for a book ID or a fresh ID that isn't one.
   */
  async add(bookId: string, image: EncodedImage): Promise<ImageAddOutcome> {
    if (!isMadeHere(image)) return { kind: 'refused' };
    const { bytes, format, width, height } = image;
    const sha256 = toHex(await this.#options.digest(bytes));
    for (let tries = 0; tries < MAX_ID_TRIES; tries++) {
      const record = { bookId, id: this.#options.newId(), sha256, format, width, height, size: bytes.length };
      const result = await this.#options.store.addImage(record, bytes, this.#options.cap ?? BOOK_IMAGE_CAP);
      if (result.kind === 'added') return { kind: 'added', reference: { id: record.id, sha256 } };
      if (result.kind === 'existing') return { kind: 'existing', reference: { id: result.image.id, sha256 } };
      // A5: a taken ID is drawn again.
      if (result.kind !== 'taken') return result;
    }
    return { kind: 'failed', error: new Error(`no free image ID in ${String(MAX_ID_TRIES)} tries`) };
  }

  /** Table G: the image `reference` names in book `bookId`, checked against its hash; rejects when the store fails (G6). */
  async load(bookId: string, reference: ImageReference): Promise<LoadedImage> {
    let stored: ImageBytesRecord | undefined;
    try {
      stored = await this.#options.store.readImageBytes(bookId, reference.id);
    } catch (error) {
      if (error instanceof StoreRecordError) return { kind: 'refused' };
      throw error;
    }
    if (stored === undefined) return { kind: 'missing' };
    if (toHex(await this.#options.digest(stored.bytes)) !== reference.sha256) return { kind: 'refused' };
    const header = readImageHeader(stored.bytes);
    if (typeof header === 'string' || header.format === 'webp') return { kind: 'refused' };
    return { kind: 'image', bytes: stored.bytes, format: header.format, width: header.width, height: header.height };
  }
}
